
import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Users,
  BriefcaseBusiness,
  AlertCircle,
  MapPin,
  Building2,
  UserRound,
  Mail,
} from "lucide-react";

import "./PartnerJobRequests.css";

const API = "/api/admin/invitations";

export default function AdminInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("PENDING");

  const loadInvitations = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(API);

      if (!response.ok) {
        throw new Error(
          `Could not load invitations (${response.status}).`
        );
      }

      const data = await response.json();
      setInvitations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        `${err.message} Make sure Spring Boot is running on port 8080.`
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const updateInvitation = async (id, action) => {
    const actionLabel = action === "approve" ? "approve" : "reject";

    if (
      !window.confirm(
        `Are you sure you want to ${actionLabel} invitation #${id}?`
      )
    ) {
      return;
    }

    setBusyId(id);
    setError("");
    setMessage("");

    try {
      const response = await fetch(`${API}/${id}/${action}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.message || `Unable to ${actionLabel} invitation.`
        );
      }

      setMessage(
        result.message ||
          `Invitation ${
            action === "approve" ? "approved" : "rejected"
          } successfully.`
      );

      await loadInvitations();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = invitations.filter(
    (item) => item.status?.toUpperCase() === "PENDING"
  ).length;

  const approvedCount = invitations.filter(
    (item) => item.status?.toUpperCase() === "APPROVED"
  ).length;

  const rejectedCount = invitations.filter(
    (item) => item.status?.toUpperCase() === "REJECTED"
  ).length;

  const filteredInvitations =
    filter === "ALL"
      ? invitations
      : invitations.filter(
          (item) => item.status?.toUpperCase() === filter
        );

  return (
    <main className="admin-invitations">
      <header className="invitation-header">
        <div>
          <span className="invitation-eyebrow">
            CERTED ADMINISTRATION
          </span>
          <h1>Job Invitations</h1>
          <p>
            Review partner invitations before notifying trainers.
          </p>
        </div>

        <button
          className="refresh-btn"
          onClick={loadInvitations}
          disabled={loading}
        >
          <RefreshCw size={17} />
          Refresh
        </button>
      </header>

      <section className="invitation-stats">
        <article className="invitation-stat">
          <span className="stat-icon pending-icon">
            <Clock size={21} />
          </span>
          <div>
            <p>Pending review</p>
            <h2>{pendingCount}</h2>
          </div>
        </article>

        <article className="invitation-stat">
          <span className="stat-icon approved-icon">
            <CheckCircle2 size={21} />
          </span>
          <div>
            <p>Approved</p>
            <h2>{approvedCount}</h2>
          </div>
        </article>

        <article className="invitation-stat">
          <span className="stat-icon rejected-icon">
            <XCircle size={21} />
          </span>
          <div>
            <p>Rejected</p>
            <h2>{rejectedCount}</h2>
          </div>
        </article>
      </section>

      {error && (
        <div className="invitation-alert error-alert">
          <AlertCircle size={19} />
          <span>{error}</span>
        </div>
      )}

      {message && (
        <div className="invitation-alert success-alert">
          <CheckCircle2 size={19} />
          <span>{message}</span>
        </div>
      )}

      <section className="invitation-panel">
        <div className="invitation-toolbar">
          <div>
            <h2>Invitation requests</h2>
            <p>
              Check each request and decide whether to approve it.
            </p>
          </div>

          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            aria-label="Filter invitations"
          >
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="ALL">All invitations</option>
          </select>
        </div>

        {loading ? (
          <div className="invitation-empty">
            <RefreshCw className="loading-icon" size={26} />
            <p>Loading invitations...</p>
          </div>
        ) : filteredInvitations.length === 0 ? (
          <div className="invitation-empty">
            <Users size={36} />
            <h3>No invitations found</h3>
            <p>There are no invitations in this category.</p>
          </div>
        ) : (
          <div className="invitation-list">
            {filteredInvitations.map((item) => {
              const status = item.status?.toUpperCase() || "PENDING";
              const isPending = status === "PENDING";

              return (
                <article className="invitation-card" key={item.id}>
                  <div className="invitation-card-top">
                    <div className="invitation-card-title">
                      <span className="job-icon">
                        <BriefcaseBusiness size={21} />
                      </span>

                      <div>
                        <h3>
                          {item.jobTitle || `Invitation #${item.id}`}
                        </h3>
                        <p>Invitation #{item.id}</p>
                      </div>
                    </div>

                    <span
                      className={`status-pill status-${status.toLowerCase()}`}
                    >
                      {status}
                    </span>
                  </div>

                  <div className="invitation-details">
                    <div>
                      <span>
                        <UserRound size={15} /> Trainer
                      </span>
                      <strong>
                        {item.trainerName || "Trainer name unavailable"}
                      </strong>
                      {item.trainerEmail && (
                        <small>
                          <Mail size={13} /> {item.trainerEmail}
                        </small>
                      )}
                      {item.trainerCity && (
                        <small>
                          <MapPin size={13} /> {item.trainerCity}
                        </small>
                      )}
                    </div>

                    <div>
                      <span>
                        <Building2 size={15} /> Partner / Company
                      </span>
                      <strong>
                        {item.companyName ||
                          item.partnerName ||
                          "Partner name unavailable"}
                      </strong>
                      {item.companyType && (
                        <small>{item.companyType}</small>
                      )}
                    </div>

                    <div>
                      <span>
                        <BriefcaseBusiness size={15} /> Job details
                      </span>
                      <strong>
                        {item.jobTitle || "Job title unavailable"}
                      </strong>
                      {item.jobLocation && (
                        <small>
                          <MapPin size={13} /> {item.jobLocation}
                        </small>
                      )}
                    </div>

                    <div>
                      <span>
                        <Clock size={15} /> Submitted
                      </span>
                      <strong>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleString()
                          : "—"}
                      </strong>
                    </div>
                  </div>

                  {item.invitationMessage && (
                    <div className="invitation-message">
                      <span>Partner's message</span>
                      <p>{item.invitationMessage}</p>
                    </div>
                  )}

                  {isPending && (
                    <div className="invitation-actions">
                      <button
                        className="reject-btn"
                        disabled={busyId === item.id}
                        onClick={() =>
                          updateInvitation(item.id, "reject")
                        }
                      >
                        <XCircle size={17} />
                        Reject
                      </button>

                      <button
                        className="approve-btn"
                        disabled={busyId === item.id}
                        onClick={() =>
                          updateInvitation(item.id, "approve")
                        }
                      >
                        <CheckCircle2 size={17} />
                        {busyId === item.id
                          ? "Processing..."
                          : "Approve & Notify"}
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
